import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';

const GAP = 4;

function CrmTabsRow({ tabs, activeKey, onChange }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const moreRef = useRef(null);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const recalcRef = useRef(null);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const direction = getComputedStyle(container).flexDirection;
    if (direction === 'column') {
      setVisibleCount(tabs.length);
      return;
    }

    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;

    const tabEls = measure.querySelectorAll('[data-measure-tab]');
    const moreEl = measure.querySelector('[data-measure-more]');
    if (!moreEl || tabEls.length === 0) return;

    const tabWidths = Array.from(tabEls).map((el) => el.offsetWidth);
    const moreWidth = moreEl.offsetWidth;

    let totalWidth = 0;
    for (let i = 0; i < tabWidths.length; i++) {
      totalWidth += tabWidths[i] + (i > 0 ? GAP : 0);
    }

    if (totalWidth <= containerWidth) {
      setVisibleCount(tabs.length);
      return;
    }

    let accumulated = 0;
    let count = 0;
    for (let i = 0; i < tabWidths.length; i++) {
      const widthWithTab = accumulated + tabWidths[i] + (i > 0 ? GAP : 0);
      const widthWithMore = widthWithTab + GAP + moreWidth;
      if (widthWithMore > containerWidth) break;
      accumulated = widthWithTab;
      count++;
    }

    setVisibleCount(Math.max(1, count));
  }, [tabs]);

  recalcRef.current = recalculate;

  useLayoutEffect(() => {
    recalculate();
  }, [recalculate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      recalcRef.current();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [moreOpen]);

  const needsOverflow = visibleCount < tabs.length;
  const visibleTabs = needsOverflow ? tabs.slice(0, visibleCount) : tabs;
  const overflowTabs = needsOverflow ? tabs.slice(visibleCount) : [];
  const isOverflowActive = overflowTabs.some((tab) => tab.key === activeKey);

  const handleOverflowSelect = (key) => {
    onChange(key);
    setMoreOpen(false);
  };

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: GAP }}
      >
        {tabs.map((tab) => (
          <span key={tab.key} data-measure-tab className="cdp-tab">
            {tab.label}
          </span>
        ))}
        <span data-measure-more className="cdp-tab">
          More
          <i className="fal fa-chevron-down" style={{ marginLeft: 6, fontSize: 12 }} aria-hidden="true" />
        </span>
      </div>

      <div ref={containerRef} className="cdp-tabs-row">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`cdp-tab ${activeKey === tab.key ? 'cdp-tab--active' : ''}`}
            aria-current={activeKey === tab.key ? 'page' : undefined}
            onClick={() => onChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}

        {needsOverflow && (
          <div ref={moreRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={`cdp-tab ${isOverflowActive ? 'cdp-tab--active' : ''}`}
              aria-haspopup="true"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((prev) => !prev)}
            >
              More
              <i
                className="fal fa-chevron-down"
                style={{ marginLeft: 6, fontSize: 12 }}
                aria-hidden="true"
              />
            </button>

            {moreOpen && (
              <div className="cdp-tabs-more-menu">
                {overflowTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`cdp-tabs-more-item ${activeKey === tab.key ? 'cdp-tabs-more-item--active' : ''}`}
                    onClick={() => handleOverflowSelect(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default CrmTabsRow;
